import * as React from "react";
import dayjs from "dayjs";
import duration, {type Duration} from "dayjs/plugin/duration";
import {MotionValue, motion, useSpring, useTransform} from "framer-motion";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
dayjs.extend(duration);

const fontSize = 16;
const padding = 4;
const height = fontSize + padding;

export function Ticker({duration}: {duration: Duration}) {
  const {t} = useTranslation();

  const days = duration.asDays();
  const hours = duration.asHours() - Math.floor(days) * 24;
  const minutes = duration.asMinutes();
  const seconds = duration.asSeconds();

  return (
    <div
      style={{fontSize}}
      className="flex items-center overflow-hidden font-semibold leading-tight -my-1"
    >
      {days >= 1 && (
        <>
          <Digit place={1} value={days} />
          <span className="pr-1 pl-[1px] md:pr-[6px]">
            {t("recipe_details.cta.versions.locks_soon.day_unit")}
          </span>
        </>
      )}
      <Digit place={10} maxValue={6} value={hours} />
      <Digit place={1} value={hours} />
      <span className="pr-1 pl-[1px] md:pr-[6px]">h</span>
      <Digit place={10} maxValue={6} value={minutes} />
      <Digit place={1} value={minutes} />
      <span className="pr-1 pl-[1px] md:pr-[6px]">min</span>
      <Digit place={10} maxValue={6} value={seconds} />
      <Digit place={1} value={seconds} />
      <span className="pr-1 pl-[1px] md:pr-[6px]">s</span>
    </div>
  );
}

function Digit({
  place,
  value,
  maxValue = 10,
}: {
  place: number;
  value: number;
  maxValue?: number;
}) {
  let valueRoundedToPlace = Math.floor(value / place);
  let animatedValue = useSpring(valueRoundedToPlace, {stiffness: 90});

  useEffect(() => {
    animatedValue.set(valueRoundedToPlace);
  }, [animatedValue, valueRoundedToPlace]);

  return (
    <div style={{height}} className="relative w-[calc(1ch_-_1px)] tabular-nums">
      {[...Array(maxValue).keys()].map((i) => (
        <Number key={i} mv={animatedValue} number={i} maxValue={maxValue} />
      ))}
    </div>
  );
}

function Number({
  mv,
  number,
  maxValue,
}: {
  mv: MotionValue;
  number: number;
  maxValue: number;
}) {
  let y = useTransform(mv, (latest) => {
    let placeValue = latest % maxValue;
    let offset = (maxValue + number - placeValue) % maxValue;

    let memo = offset * height;

    if (offset > Math.floor(maxValue / 2)) {
      memo -= maxValue * height;
    }

    return memo;
  });

  return (
    <motion.span
      style={{y}}
      className="absolute inset-0 flex items-center justify-center"
    >
      {number}
    </motion.span>
  );
}
